import { AnchorNodeData, AnchorTypeNodeData } from '../tree.model';
import { DynamicAnchorType } from '../../diagrams/dynamic-diagram/enums/dynamic-anchor-type.enum';
import { NodeType } from '../enums/node-type.enum';
import { TreeType } from '../enums/tree-type.enum';

// PrimeNG
// eslint-disable-next-line sort-imports
import type { MenuItem, MenuItemCommandEvent, TreeNode } from 'primeng/api';

const COMM_TABLE_TYPES = new Set([
    TreeType.Address,
    TreeType.AllocationElement,
    TreeType.Component,
    TreeType.ComponentCode,
    TreeType.FunctionCategory,
    TreeType.Message,
    TreeType.Scenario,
    TreeType.Segment,
    TreeType.Signal,
    TreeType.UseCase,
    TreeType.UserFunction,
    TreeType.AllocationElementDiagram,
    TreeType.CanPortData,
    TreeType.InternalPortData,
    TreeType.DirectWirePortData,
]);

const USER_CRUD_TYPES = new Set([
    TreeType.Address,
    TreeType.AllocationElement,
    TreeType.AllocationElementDiagram,
    TreeType.Component,
    TreeType.ComponentCode,
    TreeType.FunctionCategory,
    TreeType.Message,
    TreeType.Scenario,
    TreeType.Segment,
    TreeType.Signal,
    TreeType.UseCase,
    TreeType.UserFunction,
]);

export function getMenuItems(
    node: TreeNode<AnchorNodeData>,
    openModal: (node: TreeNode<AnchorNodeData>) => void,
    openCRModal: (node: TreeNode<AnchorNodeData>, event?: MenuItemCommandEvent) => void,
    openCloneSignalModal: (node: TreeNode<AnchorNodeData>) => void,
    duplicateAllocationElementDiagram?: (
        node: TreeNode<AnchorNodeData>,
        event?: MenuItemCommandEvent
    ) => void,
    onAnchorSelect?: (node: TreeNode<AnchorNodeData>) => void,
    isReadOnlyUser = false
): MenuItem[] {
    const menuItems: MenuItem[] = [];
    if (node.data) {
        if (node.type === NodeType.anchor) {
            const nodeAnchorType = node.data.anchorType;
            const nodeAnchorKey = node.data.anchor.key;

            if (isDynamicAnchorType(nodeAnchorType)) {
                menuItems.push({
                    label: 'Generate Diagram',
                    icon: 'pi pi-sitemap',
                    routerLink: `/dynamic-diagrams/${(
                        nodeAnchorType as string
                    ).toLowerCase()}/${nodeAnchorKey}`,
                });
            }

            switch (nodeAnchorType) {
                case TreeType.AllocationElementDiagram:
                    menuItems.push({
                        label: 'Open Diagram',
                        icon: 'pi pi-palette',
                        routerLink: `/static-diagrams/${nodeAnchorKey}`,
                    });
                    if (duplicateAllocationElementDiagram && !isReadOnlyUser) {
                        menuItems.push({
                            label: 'Duplicate Diagram',
                            icon: 'pi pi-copy',
                            command: (event) => duplicateAllocationElementDiagram(node, event),
                        });
                    }
                    break;
                case TreeType.Message:
                    menuItems.push({
                        label: 'Open M.S. Allocation',
                        icon: 'pi pi-list-check',
                        routerLink: `/message-signal-allocation/${nodeAnchorKey}`,
                    });
                    break;
                case TreeType.Scenario:
                    menuItems.push({
                        label: 'Open MSC',
                        icon: 'pi pi-microchip',
                        routerLink: `/msc/${nodeAnchorKey}`,
                    });
                    break;
                case TreeType.ChangeRequest:
                    {
                        const scenarioNodePath = [
                            { anchorType: TreeType.ChangeRequest, key: nodeAnchorKey },
                            { anchorType: TreeType.Scenario },
                        ];
                        const scenarioPathParam = encodeURIComponent(
                            JSON.stringify(scenarioNodePath)
                        );
                        menuItems.push({
                            label: 'Show Connected Scenarios',
                            icon: 'pi pi-list',
                            routerLink: `/summary-list/${TreeType.Scenario.toLowerCase()}`,
                            queryParams: { path: scenarioPathParam },
                        });
                        const useCaseNodePath = [
                            { anchorType: TreeType.ChangeRequest, key: nodeAnchorKey },
                            { anchorType: TreeType.UseCase },
                        ];
                        const useCasePathParam = encodeURIComponent(
                            JSON.stringify(useCaseNodePath)
                        );
                        menuItems.push({
                            label: 'Show Connected Use Cases',
                            icon: 'pi pi-list',
                            routerLink: `/summary-list/${TreeType.UseCase.toLowerCase()}`,
                            queryParams: { path: useCasePathParam },
                        });
                        menuItems.push({
                            label: 'See Change Request Details',
                            icon: 'pi pi-eye',
                            command: (event) => openCRModal(node, event),
                        });
                    }
                    break;
            }
            if (USER_CRUD_TYPES.has(nodeAnchorType!) && !isReadOnlyUser) {
                menuItems.push({
                    label: 'Edit',
                    icon: 'pi pi-pencil',
                    command: () => openModal(node),
                });
            }

            if (nodeAnchorType === TreeType.Signal && !isReadOnlyUser) {
                menuItems.push({
                    label: 'Clone Signal',
                    icon: 'pi pi-copy',
                    command: () => openCloneSignalModal(node),
                });
            }

            if (COMM_TABLE_TYPES.has(nodeAnchorType!)) {
                menuItems.push({
                    label: 'Open Comm. Table',
                    icon: 'pi pi-table',
                    routerLink: `/communication-table/${nodeAnchorType!.toLowerCase()}/${nodeAnchorKey}`,
                });
            }
            if (nodeAnchorType !== TreeType.ChangeRequest) {
                menuItems.push({
                    label: 'See Version History',
                    icon: 'pi pi-history',
                    routerLink: `/version-history/${nodeAnchorType!.toLowerCase()}/${nodeAnchorKey}`,
                });
            } else {
                menuItems.push({
                    label: 'See Change Request History',
                    icon: 'pi pi-history',
                    routerLink: `/change-request-history/${nodeAnchorKey}`,
                });
            }
            if (onAnchorSelect) {
                menuItems.forEach((item) => {
                    const originalCommand = item.command;
                    item.command = (event) => {
                        onAnchorSelect(node);
                        originalCommand?.(event);
                    };
                });
            }
            return menuItems;
        } else if (node.type === NodeType.anchorType) {
            if (USER_CRUD_TYPES.has(node.data.anchorType) && !isReadOnlyUser) {
                const anchorType = node.data.anchorType;
                menuItems.push({
                    label: `Create ${anchorType}`,
                    icon: 'pi pi-plus',
                    command: () => openModal(node),
                });
            }

            if (node.data.anchorType !== TreeType.ChangeRequest) {
                const nodePath = getFullPath(node);
                const pathParam = encodeURIComponent(JSON.stringify(nodePath));
                menuItems.push({
                    label: 'Open Summary List',
                    icon: 'pi pi-list',
                    routerLink: `/summary-list/${node.data.anchorType.toLowerCase()}`,
                    queryParams: { path: pathParam },
                });
            }

            if (node.data.anchorType === TreeType.ChangeRequest) {
                menuItems.push({
                    label: 'Open Change Request List',
                    icon: 'pi pi-list',
                    routerLink: '/change-requests',
                });
            }

            return menuItems;
        }
    }
    return [];
}

function isDynamicAnchorType(value: string): value is DynamicAnchorType {
    return Object.values(DynamicAnchorType).includes(value as DynamicAnchorType);
}

function getFullPath(
    anchorTypeNode: TreeNode<AnchorTypeNodeData>
): { anchorType: string; key?: number }[] {
    const path: { anchorType: string; key?: number }[] = [];
    let current: TreeNode | undefined = anchorTypeNode.parent;

    while (current) {
        if (current.type === 'anchor') {
            const data = current.data as AnchorNodeData;
            path.unshift({ anchorType: data.anchorType, key: data.anchor.key });
        }
        current = current.parent;
    }

    path.push({ anchorType: anchorTypeNode.data!.anchorType });
    return path;
}
